'use client';

import { useCallback } from 'react';
import type { CityCode } from '@loci/city-editions';

import { buildDiscoverHref } from '@/lib/discover/discoverHref';
import {
  useCityContext,
  useCityIsStaging,
  useCitySurface,
  useCityViewerHasSession,
} from '@/lib/city-context.client';
import { defaultCityCode } from '@/lib/cross-host-urls';

/**
 * Client twin of {@link buildDiscoverHref} (WEB-1231 / R25). Reads surface,
 * session and staging from the root-layout providers so every client
 * Discovery link resolves its host the same way on SSR and hydration.
 *
 * Returns a builder taking the Discover query (no leading `?`).
 */
export function useDiscoverHref(cityCode?: CityCode): (query?: string) => string {
  const surface = useCitySurface();
  const hasSession = useCityViewerHasSession();
  const isStaging = useCityIsStaging();
  const city = useCityContext();
  const resolvedCity = cityCode ?? city?.code ?? defaultCityCode();
  return useCallback(
    (query?: string) =>
      buildDiscoverHref({ cityCode: resolvedCity, surface, hasSession, query, isStaging }),
    [resolvedCity, surface, hasSession, isStaging],
  );
}
